import { useEffect, useRef, useState } from "react";
import { Form } from "antd";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import styles from "./DescriptionArea.module.scss";

const DescriptionArea = ({
  disabled,
  MAX_LENGTH = 5000,
  label = "Event Description",
  required = true,
}: {
  disabled: boolean;
  MAX_LENGTH: number;
  label?: string;
  required?: boolean;
}) => {
  const [charCount, setCharCount] = useState(0);

  const quillRef = useRef<ReactQuill | null>(null);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    const editorElement = quill?.root;

    if (!editorElement) return;

    const preventImagePaste = (event: ClipboardEvent) => {
      const hasImage =
        Array.from(event.clipboardData?.items ?? []).some((item) =>
          item.type.startsWith("image/"),
        ) || false;

      if (hasImage) {
        event.preventDefault();
      }
    };

    const preventImageDrop = (event: DragEvent) => {
      const hasImage =
        Array.from(event.dataTransfer?.items ?? []).some((item) =>
          item.type.startsWith("image/"),
        ) || false;

      if (hasImage) {
        event.preventDefault();
      }
    };

    editorElement.addEventListener("paste", preventImagePaste);
    editorElement.addEventListener("drop", preventImageDrop);

    return () => {
      editorElement.removeEventListener("paste", preventImagePaste);
      editorElement.removeEventListener("drop", preventImageDrop);
    };
  }, []);

  return (
    <Form.Item
      name="description"
      label={label}
      extra={
        <div
          style={{
            textAlign: "right",
            fontSize: 12,
            color: charCount >= MAX_LENGTH ? "#ff4d4f" : "rgba(0,0,0,0.45)",
          }}
        >
          {charCount}/{MAX_LENGTH}
        </div>
      }
      rules={[
        { required: required, message: "Description is required" },
        {
          validator: (_, value) => {
            if (!value) return Promise.resolve();

            const div = document.createElement("div");
            div.innerHTML = value;

            const plain = div.textContent || div.innerText || "";

            if (plain.trim().length > MAX_LENGTH) {
              return Promise.reject(
                new Error(`Maximum ${MAX_LENGTH} characters allowed`),
              );
            }

            return Promise.resolve();
          },
        },
      ]}
    >
      <ReactQuill
        ref={quillRef}
        style={{ cursor: disabled ? "not-allowed" : "auto" }}
        className={styles.customEditor}
        theme="snow"
        onChange={(_content, _delta, _source, editor) => {
          const quill = quillRef.current?.getEditor();
          const delta = quill?.getContents();

          if (quill && delta) {
            let index = 0;

            delta.ops.forEach((op) => {
              if (typeof op.insert === "string") {
                index += op.insert.length;
                return;
              }

              if (op.insert && typeof op.insert === "object" && "image" in op.insert) {
                quill.deleteText(index, 1, "silent");
                return;
              }

              index += 1;
            });
          }

          const text = editor.getText().replace(/\n$/, "");

          const length = text.length;

          // Prevent typing/pasting beyond limit
          if (length > MAX_LENGTH) {
            const quill = quillRef.current?.getEditor();

            quill?.deleteText(MAX_LENGTH, length);

            setCharCount(MAX_LENGTH);
            return;
          }

          setCharCount(length);
        }}
        modules={{
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
          ],
        }}
        formats={["bold", "italic", "underline", "list", "bullet"]}
      />
    </Form.Item>
  );
};

export default DescriptionArea;
